import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Box, Paper, Stack, TextField, Toolbar, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

export default function PrintStudyCertificates() {
  const apiBase = process.env.REACT_APP_API_BASE_URL;
  const user = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("user"));
    } catch {
      return null;
    }
  }, []);
  const [rows, setRows] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [selection, setSelection] = useState([]);
  const [rollNo, setRollNo] = useState("");
  const [studentName, setStudentName] = useState("");
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState("");
  const auth = () => ({ Authorization: `Bearer ${sessionStorage.getItem("token")}` });

  const loadRows = useCallback(async () => {
    if (!user?.client_id || !user?.college_id) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${apiBase}/api/student-service-requests/pending-study-certificates/${user.client_id}/${user.college_id}`,
        { params: { rollNo, studentName }, headers: auth() }
      );
      setRows(response.data?.data || []);
      setPaginationModel((current) => ({ ...current, page: 0 }));
      setSelection([]);
      setError("");
    } catch (e) {
      setError(e.response?.data?.message || "Unable to load pending study certificate requests");
    } finally {
      setLoading(false);
    }
  }, [apiBase, user, rollNo, studentName]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const printRequests = async (ids) => {
    const requestIds = [
      ...new Set((Array.isArray(ids) ? ids : []).map((id) => String(id).trim()).filter(Boolean)),
    ];
    if (!requestIds.length || printing) return;
    setPrinting(true);
    setError("");
    try {
      const response = await axios.post(
        `${apiBase}/api/student-service-requests/print-study-certificates/${user.client_id}/${user.college_id}`,
        { request_ids: requestIds },
        { headers: auth(), responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const selectedRow = rows.find((row) => String(row.service_request_id) === requestIds[0]);
      const dateLabel = new Intl.DateTimeFormat("en-GB").format(new Date()).replaceAll("/", "-");
      link.download =
        requestIds.length === 1
          ? `study_certificate_${String(selectedRow?.student_rollno || "student").replace(
              /[^a-zA-Z0-9_-]/g,
              "_"
            )}.pdf`
          : `study_certificates_${dateLabel}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      await loadRows();
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        try {
          setError(JSON.parse(await e.response.data.text()).message);
        } catch {
          setError("Unable to print selected requests");
        }
      } else setError(e.response?.data?.message || "Unable to print selected requests");
    } finally {
      setPrinting(false);
    }
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 75,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const rowIndex = rows.findIndex(
          (row) => String(row.service_request_id) === String(params.row.service_request_id)
        );
        return rowIndex + 1;
      },
    },
    { field: "service_request_id", headerName: "Request ID", width: 105 },
    { field: "student_rollno", headerName: "Roll No", minWidth: 120, flex: 0.8 },
    { field: "student_name", headerName: "Student Name", minWidth: 180, flex: 1.2 },
    { field: "program_name", headerName: "Program", minWidth: 130, flex: 1 },
    { field: "branch_name", headerName: "Branch", minWidth: 150, flex: 1 },
    { field: "year_number", headerName: "Year", width: 75 },
    { field: "semester_number", headerName: "Semester", width: 90 },
    {
      field: "request_date",
      headerName: "Request Date",
      width: 120,
      valueFormatter: (params) =>
        params.value ? new Date(params.value).toLocaleDateString("en-GB") : "",
    },
    { field: "paid_status", headerName: "Paid Status", width: 105 },
    { field: "status", headerName: "Status", width: 105 },
    {
      field: "actions",
      headerName: "Action",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <MDButton
          size="small"
          color="info"
          variant="gradient"
          disabled={String(row.paid_status).toLowerCase() !== "paid" || printing}
          onClick={() => printRequests([row.service_request_id])}
        >
          Print
        </MDButton>
      ),
    },
  ];

  return (
    <MDBox pt={2} pb={2} px={{ xs: 1, sm: 2 }} sx={{ width: "100%", minWidth: 0 }}>
      <Paper elevation={1} sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            PRINT STUDY CERTIFICATES
          </Typography>
          <MDButton
            color="info"
            variant="gradient"
            disabled={printing || selection.length === 0}
            onClick={() => printRequests(selection)}
          >
            {printing ? "Generating PDF..." : `Bulk Print (${selection.length})`}
          </MDButton>
        </Toolbar>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2} px={2}>
          <TextField
            size="medium"
            label="Search Roll Number"
            value={rollNo}
            onChange={(event) => setRollNo(event.target.value)}
            sx={{ width: 200 }}
          />
          <TextField
            size="medium"
            label="Search Student Name"
            value={studentName}
            onChange={(event) => setStudentName(event.target.value)}
            sx={{ width: 300 }}
          />
        </Stack>
        <Box mx={2} mb={2} sx={{ width: "auto", minWidth: 0, height: 560 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading || printing}
            getRowId={(row) => String(row.service_request_id)}
            checkboxSelection
            disableSelectionOnClick
            selectionModel={selection}
            onSelectionModelChange={(next) => setSelection(next.map(String))}
            isRowSelectable={(params) => String(params.row.paid_status).toLowerCase() === "paid"}
            page={paginationModel.page}
            pageSize={paginationModel.pageSize}
            onPageChange={(page) => setPaginationModel((current) => ({ ...current, page }))}
            onPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              "& .MuiDataGrid-cell": { borderBottom: "1px solid #e0e0e0" },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                borderBottom: "2px solid #e0e0e0",
              },
              "& .MuiDataGrid-checkboxInput": { color: "#0d47a1", opacity: 1 },
              "& .MuiDataGrid-checkboxInput.Mui-disabled": { color: "#616161", opacity: 1 },
            }}
          />
        </Box>
      </Paper>
    </MDBox>
  );
}
